<template>
  <label
    :class="{
      'at-Toggle-reverse': reverse
    }"
    :data-auto-testid="$attrs['data-auto-testid'] || null"
    :data-testid="$attrs['data-testid'] || null"
    :style="styles"
    class="at-Toggle"
  >
    <span v-if="label || $slots.label" class="at-ToggleLabel">
      <slot name="label">
        {{ label }}
      </slot>
    </span>

    <input
      v-if="isMultiple"
      v-model="checkedItems"
      :disabled="disabled"
      :value="value"
      class="at-ToggleInput"
      hidden
      type="checkbox"
    />

    <input
      v-else
      :checked="model"
      :disabled="disabled"
      :value="model"
      class="at-ToggleInput"
      hidden
      type="checkbox"
      @change="onCheckboxChange"
    />

    <span
      :class="{
        [`at-ToggleTrack-${size}`]: true,
        'at-ToggleTrack-disabled': disabled
      }"
      class="at-ToggleTrack"
    >
      <transition-group name="fade">
        <span v-if="showToggleOnIcon" class="at-ToggleTrack_Icon at-ToggleTrack_Icon-check">
          <AppIcon :height="iconsSize" :width="iconsSize" icon-name="at-toggle-on" />
        </span>
        <span v-else class="at-ToggleTrack_Icon at-ToggleTrack_Icon-cross">
          <AppIcon :height="iconsSize" :width="iconsSize" icon-name="at-toggle-off" />
        </span>
      </transition-group>
    </span>
  </label>
</template>

<script setup>
import { computed } from 'vue'

import { TOGGLE_SIZES } from '@/utils/components-configurations/app-toggle'
import { includesValue } from '@/utils/prop-validators'

import AppIcon from '@/components/ui/AppIcon/AppIcon'

defineOptions({
  name: 'AppToggle',
  inheritAttrs: false
})

const props = defineProps({
  label: {
    type: String,
    default: ''
  },

  reverse: {
    type: Boolean
  },

  color: {
    type: String,
    default: '--primary-color-next'
  },

  disabled: {
    type: Boolean
  },

  size: {
    type: String,
    default: TOGGLE_SIZES.MD,
    validator: v => includesValue(v, TOGGLE_SIZES)
  },

  value: {
    type: [String, Boolean, Number],
    default: ''
  }
})

const model = defineModel({
  type: [Boolean, Array],
  default: false
})

const isMultiple = computed(() => Array.isArray(model.value))

const showToggleOnIcon = computed(() => {
  if (isMultiple.value) {
    return model.value.includes(props.value)
  }

  return Boolean(model.value)
})

const checkedItems = computed({
  get() {
    return model.value
  },
  set(value) {
    model.value = value
  }
})

const iconsSize = computed(() => {
  return props.size === TOGGLE_SIZES.LG ? 20 : 16
})

const styles = computed(() => {
  return {
    '--at-color-100': `var(${props.color}-100)`,
    '--at-color-600': `var(${props.color}-600)`,
    '--at-color-700': `var(${props.color}-700)`
  }
})

const onCheckboxChange = e => {
  if (props.disabled) return

  model.value = e.target.checked
}
</script>

<style lang="scss" scoped>
.at-Toggle {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;

  &-reverse {
    flex-direction: row-reverse;
  }
}

.at-ToggleLabel {
  font-size: $fs-12;
  color: var(--at-label-color, $dark-1);
}

.at-ToggleInput {
  display: none;
  pointer-events: none;
  &:checked + .at-ToggleTrack {
    &:not(&-disabled) {
      background-color: var(--at-color-600);

      &:active {
        background-color: var(--at-color-700);
      }
    }

    --at-circle-left: calc(var(--at-toggle-width) - var(--at-circle-size) - var(--at-circle-shift));
  }
}

.at-ToggleTrack {
  position: relative;
  display: inline-block;
  background-color: var(--gray-blue-100);
  color: $white;
  border-radius: $border-radius-md-next;
  height: var(--at-toggle-height);
  width: var(--at-toggle-width);
  --at-circle-shift: 2px;
  cursor: pointer;
  transition: background-color $transition-fast ease, box-shadow $transition-fast ease;
  flex-shrink: 0;

  &:not(&-disabled) {
    &:hover {
      background-color: var(--at-color-600);
    }

    &:active {
      background-color: var(--at-color-700);
      box-shadow: 0 0 0 1px var(--at-color-100);
    }
  }

  &-md {
    --at-toggle-height: 20px;
    --at-toggle-width: 36px;
    --at-circle-size: 16px;
  }

  &-lg {
    --at-toggle-height: 24px;
    --at-toggle-width: 44px;
    --at-circle-size: 20px;
  }

  &:before {
    pointer-events: none;
    border-radius: 50%;
    content: '';
    position: absolute;
    background-color: $white;
    transition: left $transition-fast ease;
    top: var(--at-circle-shift);
    left: var(--at-circle-left, var(--at-circle-shift));
    z-index: 1;
    width: var(--at-circle-size);
    height: var(--at-circle-size);
    box-shadow: 0 1px 3px 0 rgba($dark-1, 0.1), 0 1px 2px 0 rgba($dark-1, 0.06);
  }

  &-disabled {
    cursor: not-allowed;

    &:before {
      background-color: var(--gray-blue-0);
    }
  }

  .at-ToggleTrack_Icon {
    position: absolute;
    top: var(--at-circle-shift);
    display: flex;
    pointer-events: none;

    &-check {
      left: var(--at-circle-shift);
    }

    &-cross {
      right: var(--at-circle-shift);
    }
  }
}
</style>
